import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "reactstrap";

import {
  FORMATOS,
  TEMAS,
  MAX_MESES_TABELA,
  desenharCard,
  nomeArquivo,
  baixarBlob,
  copiarBlob,
  suportaCopiarImagem,
} from "./shareableCard";

// Modal de prévia do card PNG do Faturamento (PO1-3632). Mostra a imagem real
// que será baixada; trocar formato ou tema redesenha na hora. Ações: Copiar
// imagem (ClipboardItem, com aviso quando o navegador não suporta) e Baixar PNG.
export default function CardPngModal({ isOpen, onClose, dados, carregandoDados, erroDados }) {
  const [formato, setFormato] = useState("retrato");
  const [tema, setTema] = useState("claro");
  const [previa, setPrevia] = useState({ url: null, truncado: false, largura: 0, altura: 0 });
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const blobRef = useRef(null);
  const urlRef = useRef(null);
  const podeCopiar = suportaCopiarImagem();

  // Redesenha a prévia quando abrir ou trocar formato/tema/dados.
  useEffect(() => {
    if (!isOpen || !dados || carregandoDados) return undefined;
    let cancelado = false;
    setGerando(true);
    setErro(null);
    desenharCard(dados, { formato, tema })
      .then((r) => {
        if (cancelado) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        blobRef.current = r.blob;
        urlRef.current = URL.createObjectURL(r.blob);
        setPrevia({ url: urlRef.current, truncado: r.truncado, largura: r.largura, altura: r.altura });
      })
      .catch((e) => {
        if (!cancelado) setErro(e.message || "Falha ao desenhar o card.");
      })
      .finally(() => {
        if (!cancelado) setGerando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [isOpen, dados, carregandoDados, formato, tema]);

  // Libera a URL do blob ao fechar.
  useEffect(() => {
    if (isOpen) return undefined;
    setAviso(null);
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      // Sem isto, ao reabrir a prévia aponta para a URL já revogada até redesenhar.
      blobRef.current = null;
      setPrevia({ url: null, truncado: false, largura: 0, altura: 0 });
    };
  }, [isOpen]);

  const baixar = () => {
    if (!blobRef.current || !dados) return;
    baixarBlob(blobRef.current, nomeArquivo(dados.embarcador, dados.de, dados.ate));
  };

  const copiar = async () => {
    if (!blobRef.current) return;
    try {
      await copiarBlob(blobRef.current);
      setAviso({ tipo: "ok", texto: "Imagem copiada. Cole no WhatsApp, Teams ou e-mail." });
    } catch (e) {
      setAviso({ tipo: "erro", texto: e.message || "Não foi possível copiar. Use Baixar PNG." });
    }
  };

  const pronto = Boolean(previa.url) && !gerando && !carregandoDados && !erro && !erroDados;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" modalClassName="modal-align-top faturamento-card-modal" fade={false}>
      <div className="faturamento-detail-header faturamento-card-modal__header">
        <div>
          <h4 style={{ margin: 0 }}>Card do volume auditado</h4>
          <small className="faturamento-muted-text">A prévia é a imagem exata que será baixada ou copiada.</small>
        </div>
        <button type="button" className="faturamento-detail-modal__close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <div className="faturamento-card-modal__body">
        <div className="faturamento-card-modal__controls">
          <div className="faturamento-card-modal__group">
            <span className="faturamento-card-modal__label">Formato</span>
            {Object.keys(FORMATOS).map((f) => (
              <button key={f} type="button" className={`faturamento-quick-range__button${formato === f ? " is-active" : ""}`} onClick={() => setFormato(f)} disabled={gerando}>
                {FORMATOS[f].rotulo}
              </button>
            ))}
          </div>
          <div className="faturamento-card-modal__group">
            <span className="faturamento-card-modal__label">Tema</span>
            {Object.keys(TEMAS).map((t) => (
              <button key={t} type="button" className={`faturamento-quick-range__button${tema === t ? " is-active" : ""}`} onClick={() => setTema(t)} disabled={gerando}>
                {TEMAS[t].rotulo}
              </button>
            ))}
          </div>
        </div>

        {carregandoDados ? (
          <div className="faturamento-loading">
            <Spinner color="info" /> <span className="faturamento-muted-text">Buscando o volume mês a mês…</span>
          </div>
        ) : null}
        {!carregandoDados && erroDados ? (
          <div className="faturamento-empty">
            <strong>Erro:</strong> {erroDados}
          </div>
        ) : null}
        {!carregandoDados && !erroDados && erro ? (
          <div className="faturamento-empty">
            <strong>Erro:</strong> {erro}
          </div>
        ) : null}

        {!carregandoDados && !erroDados ? (
          <div className={`faturamento-card-modal__preview faturamento-card-modal__preview--${formato}`}>
            {previa.url ? <img src={previa.url} alt="Prévia do card do volume auditado" style={{ opacity: gerando ? 0.5 : 1 }} /> : null}
            {gerando ? (
              <div className="faturamento-card-modal__spinner">
                <Spinner color="info" />
              </div>
            ) : null}
          </div>
        ) : null}

        {pronto && previa.truncado ? (
          <p className="faturamento-muted-text faturamento-card-modal__nota">
            O recorte tem mais de {MAX_MESES_TABELA} meses: a tabela mostra os {MAX_MESES_TABELA} últimos e os totais continuam do período inteiro.
          </p>
        ) : null}
        {!podeCopiar ? (
          <p className="faturamento-muted-text faturamento-card-modal__nota">Este navegador não permite copiar imagem para a área de transferência. Use Baixar PNG.</p>
        ) : null}
        {aviso ? <p className={`faturamento-card-modal__nota ${aviso.tipo === "erro" ? "text-danger" : "text-success"}`}>{aviso.texto}</p> : null}

        <div className="faturamento-card-modal__actions">
          {podeCopiar ? (
            <Button type="button" className="faturamento-button-secondary faturamento-button-secondary--sm" onClick={copiar} disabled={!pronto}>
              <i className="tim-icons icon-single-copy-04" /> Copiar imagem
            </Button>
          ) : null}
          <Button type="button" className="faturamento-button-primary faturamento-button-primary--sm" onClick={baixar} disabled={!pronto}>
            <i className="tim-icons icon-cloud-download-93" /> Baixar PNG
          </Button>
        </div>
      </div>
    </Modal>
  );
}
